import React, { useEffect, lazy, Suspense, useContext } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { observer } from "mobx-react-lite";
import { Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Context } from "./context";
import { stores } from "./context";
import { $api } from "./http";
import { ScrollToTop } from "./components/ScrollToTop";

// Import global components
import MainLayout from "./components/layout/MainLayout";
import Spinner from "./components/ui/Spinner";

// Lazy load pages for better performance
const LandingPage = lazy(() => import("./pages/LandingPage"));
const RestaurantPage = lazy(() => import("./pages/RestaurantPage"));
const MenuPage = lazy(() => import("./pages/MenuPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const KontaktPage = lazy(() => import("./pages/ContactPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const RecipesPage = lazy(() => import("./pages/RecipesPage"));
const SuggestionsPage = lazy(() => import("./pages/SuggestionsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const AllergensPage = lazy(() => import("./pages/AllergensPage"));
const CancelBookingPage = lazy(() =>
  import("./pages/CancelBookingPage").then((module) => ({
    default: module.CancelBookingPage,
  }))
);

// Lazy load global modals and sidebars
const CartSidebar = lazy(() => import("./components/CartSidebar"));
const LoginModal = lazy(() => import("./components/LoginModal"));
const ForgotPasswordModal = lazy(
  () => import("./components/ForgotPasswordModal")
);
const RegistrationSuccessModal = lazy(() =>
  import("./components/auth/RegistrationSuccessModal").then((module) => ({
    default: module.RegistrationSuccessModal,
  }))
);
const RequireAdmin = lazy(() =>
  import("./components/routes/RequireAdmin").then((module) => ({
    default: module.default,
  }))
);

// Lazy load admin pages
const AdminPage = lazy(() => import("./pages/Admin"));
const CommunicationPage = lazy(() => import("./pages/Admin/CommunicationPage"));
const AdminOrders = lazy(() => import("./pages/Admin/AdminOrders"));
const AdminBookingsPage = lazy(() =>
  import("./pages/Admin/AdminBookingsPage").then((module) => ({
    default: module.AdminBookingsPage,
  }))
);
const AdminProducts = lazy(() => import("./pages/Admin/AdminProducts"));
const AdminSettings = lazy(() => import("./pages/Admin/AdminSettings"));
const AdminRecipes = lazy(() => import("./pages/Admin/AdminRecipes"));
const AdminSuggestions = lazy(() => import("./pages/Admin/AdminSuggestions"));
const AdminKitchen = lazy(() => import("./pages/Kitchen"));
const AdminAnalytics = lazy(() => import("./pages/Admin/AdminAnalytics"));

const App = observer(() => {
  const { user, restaurant } = stores;
  const { ui } = useContext(Context);

  useEffect(() => {
    // Initialize UI settings on app load
    if (ui && typeof ui.init === "function") {
      ui.init();
    }
  }, [ui]);

  // Check authentication status on app load and refresh token if needed
  useEffect(() => {
    if (localStorage.getItem("token")) {
      user.checkAuth();
    }
  }, [user]);

  // Check restaurant status on app load and start polling for updates
  useEffect(() => {
    restaurant.initialize();

    return () => {
      restaurant.destroy();
    };
  }, [restaurant]);

  useEffect(() => {
    // Track a unique visit once per session
    const trackVisit = async () => {
      try {
        if (!sessionStorage.getItem("rja_visited")) {
          await $api.post("/analytics/visit");
          sessionStorage.setItem("rja_visited", "true");
        }
      } catch (error) {
        console.error("Failed to track visit:", error);
      }
    };
    trackVisit();
  }, []);

  if (user.isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ""}
    >
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <Toaster position="top-right" reverseOrder={false} />
        <Suspense fallback={<Spinner />}>
          <ScrollToTop />
          <Routes>
            {/* Public Routes with Main Layout */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<LandingPage />} />
              <Route path="menu" element={<MenuPage />} />
              <Route path="restaurant" element={<RestaurantPage />} />
              <Route path="kontakt" element={<KontaktPage />} />
              <Route path="recipes" element={<RecipesPage />} />
              <Route path="suggestions" element={<SuggestionsPage />} />
              <Route path="product/:id" element={<ProductPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="order-success" element={<OrderSuccessPage />} />
              <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="terms-of-service" element={<TermsOfServicePage />} />
              <Route path="allergens" element={<AllergensPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="booking/cancel" element={<CancelBookingPage />} />

              {/* Admin Protected Routes */}
              <Route element={<RequireAdmin />}>
                <Route path="admin" element={<AdminPage />}>
                  <Route index element={<AdminOrders />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="bookings" element={<AdminBookingsPage />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="communication" element={<CommunicationPage />} />
                  <Route path="recipes" element={<AdminRecipes />} />
                  <Route path="suggestions" element={<AdminSuggestions />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                </Route>
              </Route>
            </Route>

            {/* Kitchen route */}
            <Route path="kitchen" element={<AdminKitchen />} />

            {/* Auth routes */}
            <Route path="/login" element={<AuthPage />} />
            <Route path="/registration" element={<AuthPage />} />
            <Route
              path="/reset-password/:token"
              element={<ResetPasswordPage />}
            />
          </Routes>
        </Suspense>

        {/* Global Modals and Sidebars */}
        <CartSidebar />
        <LoginModal />
        <ForgotPasswordModal />
        <RegistrationSuccessModal
          isOpen={user.isRegistrationSuccessModalOpen}
          email={user.registeredEmail}
          onClose={() => user.closeRegistrationSuccessModal()}
          onGoToLogin={() => {
            user.closeRegistrationSuccessModal();
            user.openLoginModal();
          }}
        />
      </div>
    </GoogleReCaptchaProvider>
  );
});

export default App;
